<template>
  <BaseConfirm type="success" icon-type="badge" title="Quick Order" :visible="visible" size="max-w-xl"
    custom-icon-name="heroicons:bolt-solid" @opened="onOpened" @close="onClose">
    <template #action>
      <BaseButton :disabled="!prompt" fullwidth color="success" @click="onProcess">Proses</BaseButton>
      <BaseButton v-if="parsedVisible" :disabled="!prompt || loadingProcessAi" fullwidth color="success"
        :loading="loadingProcessAi" @click="onProcessAi">Proses Dengan AI</BaseButton>
      <BaseButton v-if="parsedVisible" :disabled="!completed || loading" fullwidth color="success" :loading="loading"
        @click="onSubmit">Buat Order</BaseButton>
      <BaseButton fullwidth color="white" @click="$emit('close')">Batal</BaseButton>
    </template>
    <template #content>
      <BaseInput tag="textarea" rows="5" inset label="Deskripsi order"
        placeholder="Buat order dari gudang PUSAT ke stockist AB.001, daftar barangnya adalah NASA 10"
        v-model="prompt" />

      <dl v-if="parsedVisible" class="grid gap-6 sm:grid-cols-3">
        <div class="w-full text-left">
          <dt class="text-xs text-gray-700">Kode PUSAT/KP</dt>
          <dd class="mt-1 text-sm font-bold text-gray-900">
            {{ parsed.originWarehouseCode }}
          </dd>
        </div>
        <div class="w-full text-left">
          <dt class="text-xs text-gray-700">Kode stockist</dt>
          <dd class="mt-1 text-sm font-bold text-gray-900">
            {{ parsed.destinationOfficeCode }}
          </dd>
        </div>
        <div class="w-full text-left">
          <dt class="text-xs text-gray-700">Metode Pengiriman</dt>
          <dd class="mt-1 text-sm font-bold text-gray-900">
            {{ parsed.orderShipment }}
          </dd>
        </div>
        <div class="w-full text-left col-span-full">
          <dt class="text-xs text-gray-700">Barang</dt>
          <Datatable class="mt-1" compact :columns="[
            { id: 'code', name: 'Kode' },
            { id: 'qty', name: 'Jumlah' }
          ]">
            <template #tbody="{ classes }">
              <tr v-for="product in parsed.products" :key="product.code">
                <td :class="classes.td">{{ product.code }}</td>
                <td :class="classes.td">{{ product.qty }}</td>
              </tr>
            </template>
          </Datatable>
        </div>
      </dl>
    </template>
  </BaseConfirm>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { requestMixin } from '../../mixins/request/request.js';
import BaseConfirm from '../base/BaseConfirm.vue';

export default {
  mixins: [requestMixin],
  components: {
    BaseConfirm,
  },
  props: {
    visible: Boolean
  },
  emits: ['close'],
  data() {
    return {
      prompt: null,
      loading: false,
      loadingProcessAi: false,
      queueId: null,
      metaLoaded: false,
      meta: {
        kp_warehouses_code: null,
        products_code: null
      },
      parsedVisible: false,
      parsed: {
        originWarehouseCode: null,
        destinationOfficeCode: null,
        orderShipment: null,
        products: []
      }
    }
  },
  computed: {
    ...mapGetters({
      getChannelName: 'auth/getChannelName',
    }),
    completed() {
      return this.parsed.destinationOfficeCode && this.parsed.orderShipment && this.parsed.products.length > 0
    }
  },
  methods: {
    ...mapActions({
      createAlert: 'alert/createAlert'
    }),
    async loadMeta() {
      if (!this.metaLoaded) {
        const [res, err] = await this.request(`/api/v1/orders/-actions/quick-order-meta`)

        if (!err) {
          this.meta = res
        }
      }
    },
    onOpened() {
      this.prompt = null
      this.queueId = null
      this.loading = false
      this.loadingProcessAi = false
      this.parsedVisible = false

      this.parsed.originWarehouseCode = null
      this.parsed.destinationOfficeCode = null
      this.parsed.orderShipment = null
      this.parsed.products = []

      this.loadMeta()
    },
    onClose() {
      this.$emit('close')
      this.stopListenQueue()
    },
    onProcess() {
      const originWarehouseCode = this.prompt.match(new RegExp(String.raw`\b(?:${this.meta.kp_warehouses_code.join('|')})\b`, 'gmi'))
      const destinationOfficeCode = this.prompt.match(/\b(?:A|AA|AB|AD|AE|AG|B|BA|BB|BD|BE|BG|BH|BK|BL|BM|BN|BP|D|DA|DB|DC|DD|DE|DG|DH|DK|DL|DM|DN|DP|DR|DT|DW|EA|EB|ED|E|F|G|H|K|KB|KH|KT|KU|L|M|N|P|PA|PB|R|S|T|W|Z)\.\d+\b/gmi)
      const orderShipment = this.prompt.match(/(kirim|delivery)/gmi)
      const products = this.prompt.match(new RegExp(String.raw`\b(?:${this.meta.products_code.join('|')})(?:\s+|\s*:\s*)(?:\d+|x\d+)\b`, 'gmi'))

      this.parsed.originWarehouseCode = originWarehouseCode ? originWarehouseCode[0] : null
      this.parsed.destinationOfficeCode = destinationOfficeCode ? destinationOfficeCode[0] : null
      this.parsed.orderShipment = orderShipment ? 'dikirim' : 'diambil'
      this.parsed.products = !products
        ? []
        : products.map(product => {
          const [, code, qty] = product.match(/^([A-Z0-9]+)\s*:?\s*x?(\d+)$/im)

          return {
            code,
            qty
          }
        })

      this.parsedVisible = true
    },
    async onProcessAi() {
      this.loadingProcessAi = true

      const [res, err] = await this.request(`/api/v1/orders/-actions/quick-order-parse`, {
        method: 'post',
        data: {
          prompt: this.prompt
        }
      })

      if (err) {
        this.createAlert({
          status: 'error',
          data: this.getRequestErrorMessage(err)
        })

        this.loadingProcessAi = false
      } else {
        this.queueId = res.queue_id
        this.listenQueue()
      }
    },
    onNotificationCreated(e) {
      if (e.type === 'App\\Notifications\\ProcessParseQuickOrderUpdated' && e.queue_id === this.queueId) {
        if (e.error) {
          this.createAlert({
            status: 'error',
            data: this.getRequestErrorMessage(e.error)
          })

          this.queueId = null
          this.stopListenQueue()
        } else {
          this.parsed.originWarehouseCode = e.data.origin_office_code
          this.parsed.destinationOfficeCode = e.data.stockist_code
          this.parsed.orderShipment = e.data.shipment_type === 'delivery' ? 'dikirim' : 'diambil'
          this.parsed.products = e.data.items
            .filter(item => this.meta.products_code.includes(item.product_code))
            .map(item => ({ code: item.product_code, qty: item.qty }))

          this.loadingProcessAi = false

          this.stopListenQueue()
        }
      }
    },
    async onSubmit() {
      this.loading = true

      const [res, err] = await this.request(`/api/v1/orders/-actions/quick-order-submit`, {
        method: 'post',
        data: {
          origin_office_code: this.parsed.originWarehouseCode ?? 'pusat',
          stockist_code: this.parsed.destinationOfficeCode,
          shipment_type: this.parsed.orderShipment === 'dikirim' ? 'delivery' : 'pickup',
          products: this.parsed.products
            .map(item => ({ product_code: item.code, qty: item.qty }))
        }
      })

      if (err) {
        this.createAlert({
          status: 'error',
          data: this.getRequestErrorMessage(err)
        })
      } else {
        this.$router.push({ name: 'penjualan.edit', params: { id: res.data.id } })
      }

      this.loading = false
    },
    listenQueue() {
      const userChannel = this.$pusher.channel(this.getChannelName);

      userChannel.bind(
        'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
        this.onNotificationCreated
      );
    },
    stopListenQueue() {
      const userChannel = this.$pusher.channel(this.getChannelName);

      if (userChannel) {
        userChannel.unbind(
          'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
          this.onNotificationCreated
        );
      }
    }
  },
  destroyed() {
    this.stopListenQueue()
  }
}
</script>